import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuthorization } from 'core/context';
import { useToast } from 'core/toast';
import { getApplicant, getApplications, getCompleteApplication } from 'core/api';
import { Terms, DocumentUpload, Summary } from './Steps';
import { Data } from 'core/utilities';
import { Spinner } from 'view/components/layout';
import { Container, UnderwritingCard } from './Underwriting.style';

enum UnderwritingSteps
{
    TERMS,
    DOCUMENT_UPLOAD,
    SUMMARY
}

export const Underwriting: React.FunctionComponent = () => {
    const history = useHistory();
    const authState = useAuthorization();
    const [ loading, setIsLoading ] = useState(true);
    const [ initialLoad, setInitialLoad ] = useState(true);
    const [ currentStep, setCurrentStep ] = useState<UnderwritingSteps>(UnderwritingSteps.TERMS);
    const [ complete, setComplete ] = useState<Data>({});
    const [ applicationId, setApplicationId ] = useState<string>('');
    const [ applicantId, setApplicantId ] = useState<string>('');
    const { setToastMessage } = useToast();

    const stepNavigator = useCallback((increment : boolean) => {
        return () => {
            if (increment) setCurrentStep(s =>  s + 1);
            else setCurrentStep(s => s - 1);
        };
    }, [ currentStep, setCurrentStep ]);

    useEffect(() => {
        if (!initialLoad) {
            if (currentStep < UnderwritingSteps.TERMS || currentStep > UnderwritingSteps.SUMMARY) {
                history.push('/dashboard');
            } else {
                setIsLoading(false);
            }   
        }
    }, [ initialLoad, currentStep, history, setIsLoading ]);

    useEffect(() => {
        let fetchSucceeded = false;
        let fetchTimedOut = false;

        (async () => {
            const { applicant } = await getApplicant(authState);
            const { application } = await getApplications(authState);
            const { complete } = await getCompleteApplication(authState, applicant?.applicationId ?? '');

            if (!fetchTimedOut) {
                // Only application in the 'underwriting' state can complete this flow
                if (application?.status === 'p' || application?.status === 'u') {
                    fetchSucceeded = true;
                    setComplete(complete);
                    setApplicationId(applicant?.applicationId ?? '');
                    setApplicantId((applicant?.id) ?? '');
                    setIsLoading(false);
                    setInitialLoad(false);
                } else {
                    history.push('/dashboard');
                }
            }
        })();

        // Notify & redirect the user if fetching their application takes longer than 60s
        const timeoutId = setTimeout(() => {
            if (!fetchSucceeded) {
                fetchTimedOut = true;
                setToastMessage();
                history.push('/dashboard');
            }
        }, 1000 * 60);

        return(() => clearTimeout(timeoutId));
    }, [ setToastMessage, setApplicationId, setApplicantId, setIsLoading, setInitialLoad ]);

    return (
        <Container isLoading={loading || initialLoad}>
            <UnderwritingCard height={620} docUploadStep={currentStep === UnderwritingSteps.DOCUMENT_UPLOAD}>
                {loading ? (
                    <Spinner css='height: inherit; margin-top: 175px;' />
                ) : currentStep === UnderwritingSteps.TERMS ? (
                    <Terms stepNavigator={stepNavigator} />
                ) : currentStep === UnderwritingSteps.DOCUMENT_UPLOAD ? (
                    <DocumentUpload
                        complete={complete}
                        applicationId={applicationId}
                        applicantId={applicantId}
                        stepNavigator={stepNavigator}
                    />
                ) : (
                    <Summary stepNavigator={stepNavigator} />
                )}
            </UnderwritingCard>
        </Container>
    );
};
